/**
 * Ryan O'Dowd
 * 2024-07-28
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useMemo,
} from 'react';
import {
  ScrollView,
  View,
} from 'react-native';
import BookProgress from '@components/BookProgress';
import CPassage from '@classes/Passage';
import ListItem from '@components/ListItem';
import PassageDisplay from '@components/PassageDisplay';
import {
  READ_ROUTE,
} from '@utils/store/actions';
import Text from '@components/common/Text';
import getStyles from './styles';
import hooks from '@hooks';

const PassagePreview = (props: PassagePreviewProps) => {
  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles);

  const regexString = props.searchTerms.map((st) => st.replace(/[.?]/g, '')).filter((st) => st).map((st, index) => (st.length <= 3 && index !== props.searchTerms.length - 1) ? `(\\b${st.replace("'", '\u2019')}\\b)` : `(${st.replace("'", '\u2019')})`).join('|');
  const regex = new RegExp(regexString, 'gi');
  const headingParts = props.heading?.split(regex) || [];
  const verseParts = props.verse_text?.trim().split(regex).filter((part) => part); // @TODO: does this work if there's no text for this verse? is that even possible for John 5:4 to match, for example? maybe when we're matching on references too?

  const partsToJsx = (text, textStyles) => {
    return (
      <Text style={textStyles}>
        {text.filter((part) => part).map((part, i) => {
          const isMatch = regex.test(part); // @TODO: ignore accents and tildes (spanish translation
          const previousCharacterIsASpace = isMatch && i > 0 && text[i - 1] && text[i - 1][text[i - 1].length - 1] === ' ';
          const nextCharacterIsASpace = isMatch && i < (text.length - 1) && text[i + 1] && text[i + 1][0] === ' ';

          return (
            <Text key={i} style={[textStyles, isMatch && styles.highlightedText, !previousCharacterIsASpace && styles.noLeftPadding, !nextCharacterIsASpace && styles.noRightPadding]}>
              {part}
            </Text>
          );
        })}
      </Text>
    );
  };

  const referencePreviewInner = useMemo(() => {
    if (!props.passage.chapter_number) {
      return (
        <BookProgress
          book={CPassage.getBookById(props.passage.book_id)}
          onChapterNumberSelect={(chapterNumber) => {
            navigation.navigate(READ_ROUTE, {
              chapterId: CPassage.getChapterByBookIdAndNumber(props.passage.book_id, chapterNumber).id,
            });
          }}
        />
      );
    } else if (!props.passage.start_verse_number) {
      return (
        <Text style={styles.verseText}>Go to {props.passage.prettyPassageName()}</Text>
      );
    }

    return (
      <PassageDisplay
        passages={[props.passage]}
        showFormatting={false}
        showFootnotes={false}
        showHeadings={false}
        hideLicensing={true}
        showWrinkleBadges={false}
        isPressDisabled={true}
      />
    );
  }, [props.passage, styles.verseText, navigation]);

  return (
    <ListItem
      containerStyles={styles.container}
      disabled={!props.passage.chapter_number}
      onPress={() => {
        navigation.navigate(READ_ROUTE, {
          chapterId: CPassage.getChapterByBookIdAndNumber(props.passage.book_id, props.passage.chapter_number).id,
          contextPassage: props.passage.chapter_number ? props.passage : null,
        });
      }}
    >
      <View style={styles.photosAndNamesRow}>{/* @TODO: rename all styles */}
        <View style={styles.referenceWrapper}>
          <Text style={styles.referenceText}>{props.passage.prettyPassageName()}</Text>
          {!props.isReferencePreview && <Text style={styles.translationText}>({props.passage.getTranslation().abbreviation})</Text>}
        </View>
        {props.isReferencePreview
          ? (
            <ScrollView
              keyboardShouldPersistTaps='handled'
              contentContainerStyle={props.passage.chapter_number && styles.passageDisplayWrapper}
            >
              {referencePreviewInner}
            </ScrollView>
          ) : (
            <View style={styles.textWrapper}>
              {props.heading && partsToJsx(headingParts, styles.headerText)}
              {partsToJsx(verseParts, styles.verseText)}
            </View>
          )
        }
      </View>
    </ListItem>
  );
};

class PassagePreviewProps {
  searchTerms: string[];
  passage: any; // @TODO: CPassage;

  heading?: string;
  verse_text?: string;
  isReferencePreview?: string;
};

export default PassagePreview;
